import { useCallback, useEffect, useState } from 'react';

import { Button, Input, Select, useDisclosure, useToast } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { HttpStatusCode } from 'axios';

import AddIcon from '@/assets/AddIcon';
import ModalDelete from '@/components/Modals/ModalDelete';
import TableUser from '@/components/Tables/TableUser';
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, DURATION_TOAST, COLORS, USER_STATUS } from '@/common/constants';
import { deleteInspectionUser, getAllInspectionUsers } from '@/services/inspectionUserService';
import { deleteShipOwner } from '@/services/shipOwnerService';
import { deleteInspector } from '@/services/inspectorService';
import { deleteRefData } from '@/services/referenceDataService';
import { getRoleData } from '@/services/roleService';
import { useAppSelector } from '@/redux/hooks';
import { RootState } from '@/redux/store';
import { Option } from '@/utils/select';

type UserItem = {
    id: string;
    role: string;
    companyId?: string;
    userName: string;
    firstName: string;
    lastName: string;
    userType?: string;
    status: number;
    companyStatus: number;
}

type RoleOption = {
    value: string;
    label: string;
}

const UserList = () => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const navigate = useNavigate();
    const name = useAppSelector((state: RootState) => state.account.name);

    const LIST_STATUS: Option[] = [
        //{ value: '', label: 'All Status' },
        { value: String(USER_STATUS.ACTIVE), label: 'Active' },
        { value: String(USER_STATUS.INACTIVE), label: 'Inactive' },
    ]

    const [currentPageNumber, setCurrentPageNumber] = useState(DEFAULT_PAGE_NUMBER);
    const [currentPageSize, setCurrentPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [totalPageNumber, setTotalPageNumber] = useState(1);
    const [id, setId] = useState('');
    const [role, setSelectedRole] = useState('');
    const [searchValue, setSearchValue] = useState('');
    const [roleFilter, setRoleFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [listUsers, setListUsers] = useState<UserItem[]>([]);
    const [listRole, setListRole] = useState<RoleOption[]>([]);
    const [deleteTitle, setDeleteTitle] = useState('Delete it?');
    const [deleteMode, setDeleteMode] = useState<'user' | 'company'>('user');

    const handleNavAdd = () => {
        navigate('/user-management/users/add');
    }

    const handleChangeSearch = (value: string) => {
        setSearchValue(value);
    }

    const handleSubmitSearch = () => {
        getData();
    }

    const handleChangeRole = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setRoleFilter(e.target.value);
    }

    const handleEdit = (item: UserItem) => {
        navigate('/user-management/users/edit', { state: {
            id: item.id,
            role: item.role,
            inspectingCompany: item.role === "inspectingcompany" ? item.companyId : '',
            shipOperator: item.role === "shipoperator" ? item.companyId : '',
            requestingCompany: item.role === "requestor" ? item.companyId : '',
            email: item.userName,
            firstName: item.firstName,
            lastName: item.lastName,
            userType: item.userType,
            status: item.status,
        } });
    }

    const handleViewPrivilege = (userId: string, roleId: string, firstName: string, lastName: string, companyName: string) => {
        navigate('/user-management/users/privilege', { state: { userId, roleId, firstName, lastName, companyName } });
    }

    const getRoleEntityLabel = (roleName: string) => {
        switch (roleName) {
            case 'shipoperator':
                return 'Ship Operator';
            case 'inspectingcompany':
                return 'Inspecting Company';
            case 'requestor':
                return 'Requestor';
            default:
                return 'Data';
        }
    }

    const handleDelete = (id: string, role: string) => {
        setId(id);
        setSelectedRole(role);
        setDeleteMode('user');
        setDeleteTitle('Delete User?');
        onOpen();
    }

    const handleRoleBaseDelete = (id: string, role: string) => {
        setId(id);
        setSelectedRole(role);
        setDeleteMode('company');
        setDeleteTitle(`Delete ${getRoleEntityLabel(role)}?`);
        onOpen();
    }

    const handleConfirmDelete = async () => {
        let response: { status?: number } = {};

        if (deleteMode === 'company') {
            const modifiedBy = name || 'System';

            switch (role) {
                case 'shipoperator':
                    response = await deleteShipOwner({ shipOwnerId: id, modifiedBy });
                    break;
                case 'inspectingcompany':
                    response = await deleteInspector({ inspectorId: id, modifiedBy });
                    break;
                case 'requestor':
                    response = await deleteRefData({ id, modifiedBy });
                    break;
                default:
                    response = { status: HttpStatusCode.BadRequest };
                    break;
            }

            if (response.status === HttpStatusCode.Ok) {
                getData();
                toast({
                    title: `${getRoleEntityLabel(role)} deleted.`,
                    description: `We've deleted the ${getRoleEntityLabel(role).toLowerCase()} for you.`,
                    status: 'success',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
            } else {
                toast({
                    title: `${getRoleEntityLabel(role)} failed to delete.`,
                    description: `We failed to delete the ${getRoleEntityLabel(role).toLowerCase()} for you.`,
                    status: 'error',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
            }
        } else {
            response = await deleteInspectionUser(id, role);
            if (response.status === HttpStatusCode.Ok) {
                getData();
                toast({
                    title: 'User deleted.',
                    description: "We've deleted user for you.",
                    status: 'success',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
            } else {
                toast({
                    title: 'User failed to delete.',
                    description: "We failed to delete user for you.",
                    status: 'error',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
            }
        }

        onClose();
    }

    const getData = useCallback(async () => {
        setIsLoading(true);
        try {
            const params = {
                role: roleFilter,
                status: statusFilter,
                search: searchValue,
                pageNumber: currentPageNumber,
                pageSize: currentPageSize
            }
            const response = await getAllInspectionUsers(params);
            if (response.status === HttpStatusCode.Ok) {
                setListUsers(response.data);
                setTotalPageNumber(response?.totalPage);
                setHasNextPage(response?.hasNextPage);
            } else {
                setTotalPageNumber(0);
                toast({
                    title: response?.status || 'Failed to load list users.',
                    description: response?.message || "We failed to load list users for you.",
                    status: 'error',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });

                setListUsers([]);
            }
        } finally {
            setIsLoading(false);
        }
    }, [currentPageNumber, currentPageSize, roleFilter, searchValue, statusFilter, toast])

    const getListRole = async () => {
        const response = await getRoleData();
        if (response.status === HttpStatusCode.Ok) {
            const formattedOptions = response.data.map((item: { roleId: string; roleName: string }) => ({ value: item.roleId, label: item.roleName }));
            setListRole(formattedOptions);
        }
    }

    useEffect(() => {
        getListRole();
    }, []);

    useEffect(() => {
        getData();
    }, [getData]);

    return (
        <>
            <ModalDelete title={deleteTitle} isOpen={isOpen} onClose={onClose} handleConfirmDelete={handleConfirmDelete} />
            <div className="table-container">
                <div className="table-filter-action-container">
                    <div className="table-filter-container">
                        <div className="table-filter-input filter-input-width-9">
                            <Input placeholder='Search' onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangeSearch(e.target.value)} />
                        </div>
                        <div className="table-filter-input filter-input-width-6">
                            <Select
                                placeholder="All Role"
                                value={roleFilter}
                                onChange={(value) => handleChangeRole(value)}
                            >
                                {listRole.map((item: RoleOption) => (
                                    <option key={item.value} value={item.value}>{item.label}</option>
                                ))}
                            </Select>
                        </div>
                        <div className="table-filter-input filter-input-width-5">
                            <Select
                                placeholder="All Status"
                                value={statusFilter}
                                onChange={(event) => setStatusFilter(event.target.value)}
                            >
                                {LIST_STATUS.map((item: Option) => (
                                    <option key={item.value} value={item.value}>{item.label}</option>
                                ))}
                            </Select>
                        </div>
                        <div>
                            <Button className='table-search-button' bgColor={COLORS.BUTTON.BACKGROUND} color={COLORS.BUTTON.FOREGROUND} variant='solid' borderRadius={10} onClick={handleSubmitSearch}>
                                Search
                            </Button>
                        </div>
                        <div className="table-filter-separator"/>
                        <Button leftIcon={<AddIcon />} bgColor={COLORS.BUTTON.BACKGROUND} color={COLORS.BUTTON.FOREGROUND} className='table-add-button' variant='solid' borderRadius={10} onClick={handleNavAdd}>
                            Add User
                        </Button>
                    </div>
                </div>
                <TableUser
                    dataList={listUsers}
                    isLoading={isLoading}
                    handleDelete={handleDelete}
                    handleRoleBaseDelete={handleRoleBaseDelete}
                    handleEdit={handleEdit}
                    handleViewPrivilege={handleViewPrivilege}
                    currentPageSize={currentPageSize}
                    handleChangePageSize={setCurrentPageSize}
                    handleChangePageNumber={setCurrentPageNumber}
                    currentPageNumber={currentPageNumber}
                    totalPageNumber={totalPageNumber}
                    hasNextPage={hasNextPage}
                />
            </div>
        </>
    )
}

export default UserList;
